// src/components/BCP/BCPModals.tsx - Modal components for BCP form
import React from 'react';
import { useNavigate } from 'react-router-dom';
import AIAnalysisModal from './AIAnalysisModal';
import DocumentPreview from './DocumentPreview';
import SubscriptionUpgradeModal from '../SubscriptionUpgradeModal';
import { AnalysisStruct } from '../../services/api';

interface BCPModalsProps {
  // AI Analysis Modal
  showAnalysisModal: boolean;
  setShowAnalysisModal: (show: boolean) => void;
  analysis: AnalysisStruct | null;
  bcpName: string;

  // Document Preview Modal
  showPreviewModal: boolean;
  setShowPreviewModal: (show: boolean) => void;
  previewBCPId: number | null;
  previewBCPName: string;

  // Subscription Upgrade Modal
  showUpgradeModal: boolean;
  setShowUpgradeModal: (show: boolean) => void;

  // BCP Generation Modal
  showGenerationModal: boolean;
  setShowGenerationModal: (show: boolean) => void;
  generationStep: 'saving' | 'generating' | 'complete';
  generatedBCPId: number | null;
  formData: {
    business_name: string;
  };
  onPreviewBCP: (bcpId: number, bcpName: string) => void;
}

const BCPModals: React.FC<BCPModalsProps> = ({
  showAnalysisModal,
  setShowAnalysisModal,
  analysis,
  bcpName,
  showPreviewModal,
  setShowPreviewModal,
  previewBCPId,
  previewBCPName,
  showUpgradeModal,
  setShowUpgradeModal,
  showGenerationModal,
  setShowGenerationModal,
  generationStep,
  generatedBCPId,
  formData,
  onPreviewBCP,
}) => {
  const navigate = useNavigate();

  return (
    <>
      {/* AI Analysis Modal */}
      <AIAnalysisModal
        isOpen={showAnalysisModal}
        onClose={() => setShowAnalysisModal(false)}
        analysis={analysis}
        bcpName={bcpName}
        isLoading={false}
      />

      {/* Document Preview Modal */}
      <DocumentPreview
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        bcpId={previewBCPId || 0}
        bcpName={previewBCPName}
      />

      {/* Subscription Upgrade Modal */}
      <SubscriptionUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        requiredFeature="essential"
        featureName="Generate BCP"
      />

      {/* BCP Generation Modal */}
      {showGenerationModal && (
        <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
          <div className="mx-4 w-full max-w-md bg-white rounded-2xl shadow-2xl">
            <div className="p-8 text-center">
              {generationStep === 'saving' && (
                <>
                  <div className="mx-auto mb-6 w-16 h-16 rounded-full border-4 border-blue-200 animate-spin border-t-blue-600"></div>
                  <h3 className="mb-2 text-xl font-semibold text-gray-900">Saving Your BCP</h3>
                  <p className="text-gray-600">Please wait while we save your business continuity plan...</p>
                </>
              )}

              {generationStep === 'generating' && (
                <>
                  <div className="mx-auto mb-6 w-16 h-16 rounded-full border-4 border-purple-200 animate-spin border-t-purple-600"></div>
                  <h3 className="mb-2 text-xl font-semibold text-gray-900">Generating BCP</h3>
                  <p className="text-gray-600">Our AI is analyzing your data and generating your comprehensive business continuity plan...</p>
                </>
              )}

              {generationStep === 'complete' && (
                <>
                  <div className="flex justify-center items-center mx-auto mb-6 w-16 h-16 bg-green-100 rounded-full">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="mb-2 text-xl font-semibold text-gray-900">BCP Generated Successfully!</h3>
                  <p className="mb-6 text-gray-600">Your business continuity plan has been analyzed and successfully created.</p>

                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        setShowGenerationModal(false);
                        navigate('/bcp');
                      }}
                      className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
                    >
                      View Plans
                    </button>
                    {generatedBCPId && (
                      <button
                        onClick={() => {
                          onPreviewBCP(generatedBCPId, formData.business_name);
                          setShowGenerationModal(false);
                        }}
                        className="flex-1 px-4 py-2 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
                      >
                        Preview BCP
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BCPModals;
